import { useMutation, useQuery } from '@tanstack/react-query'
import { CertificatesFilterParams, CreateCertificateParams, UpdateCertificateParams } from '@/types/certificate'
import { useCertificatesStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useCertificates = (params: CertificatesFilterParams) => {
  const fetchCertificates = useCertificatesStore(state => state.fetchCertificates)

  return useQuery({
    queryKey: ['certificates', params],
    queryFn: () => fetchCertificates(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useCertificate = (id: string) => {
  const getCertificate = useCertificatesStore(state => state.getCertificate)

  return useQuery({
    queryKey: ['certificate', id],
    queryFn: () => getCertificate(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateCertificate = () => {
  const createCertificate = useCertificatesStore(state => state.createCertificate)

  return useMutation({
    mutationFn: (params: CreateCertificateParams) => createCertificate(params),
    onSuccess: () => {}
  })
}

export const useUpdateCertificate = () => {
  const updateCertificate = useCertificatesStore(state => state.updateCertificate)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateCertificateParams }) => updateCertificate(id, params),
    onSuccess: () => {}
  })
}

export const useSortCertificate = () => {
  const sortCertificate = useCertificatesStore(state => state.sortCertificate)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortCertificate(id, type),
    onSuccess: () => {}
  })
}

export const useDeleteCertificate = () => {
  const deleteCertificate = useCertificatesStore(state => state.deleteCertificate)

  return useMutation({
    mutationFn: (id: string) => deleteCertificate(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteCertificates = () => {
  const bulkDeleteCertificates = useCertificatesStore(state => state.bulkDeleteCertificates)
  const deselectAllCertificates = useCertificatesStore(state => state.deselectAllCertificates)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteCertificates(ids),
    onSuccess: response => {
      deselectAllCertificates()
      handleBulkDeleteSuccess(response)
    }
  })
}
